import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQuery } from './auth'

const prefix = 'applications/v1.0'

function applicationFromProposalMutationResponse(
  response: ApplicationProposalDraftResponse,
): ApplicationDetail {
  if ('application' in response) {
    const { application, proposal } = response
    if (proposal) {
      return { ...application, proposal }
    }
    return application
  }
  return response
}

export const applicationsManagerApi = createApi({
  baseQuery: baseQuery(),
  reducerPath: 'applicationsManagerApi',
  endpoints: (builder) => ({
    assignManager: builder.mutation<ApplicationDetail, string>({
      query: (publicId) => ({
        url: `${prefix}/adm/applications/${publicId}/assign/`,
        method: 'POST',
      }),
    }),
    assignExecutor: builder.mutation<
      ApplicationDetail,
      ApplicationAssignExecutorParams
    >({
      query: ({ publicId, ...body }) => ({
        url: `${prefix}/adm/applications/${publicId}/executor/`,
        method: 'PATCH',
        body,
      }),
    }),
    updateCheckItem: builder.mutation<
      ApplicationDetail,
      ApplicationUpdateCheckItemParams
    >({
      query: ({ publicId, kind, ...body }) => ({
        url: `${prefix}/adm/applications/${publicId}/check-items/${kind}/`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: ApplicationCheckItemResponse) => {
        if (response && typeof response === 'object' && 'application' in response && response.application) {
          return response.application
        }
        return response as ApplicationDetail
      },
    }),
    uploadCheckItemDocument: builder.mutation<
      ApplicationDetail,
      ApplicationUploadDocumentParams
    >({
      query: ({ publicId, kind, file }) => {
        const body = new FormData()
        body.append('file', file)
        body.append('title', file.name)
        return {
          url: `${prefix}/adm/applications/${publicId}/check-items/${kind}/documents/`,
          method: 'POST',
          body,
        }
      },
      transformResponse: (response: ApplicationDocumentResponse) => response.application,
    }),
    deleteCheckItemDocument: builder.mutation<
      ApplicationDetail,
      ApplicationDeleteDocumentParams
    >({
      query: ({ publicId, kind, fileId }) => ({
        url: `${prefix}/adm/applications/${publicId}/check-items/${kind}/documents/${fileId}/`,
        method: 'DELETE',
      }),
    }),
    createClarification: builder.mutation<
      ApplicationDetail,
      ApplicationCreateClarificationParams
    >({
      query: ({ publicId, ...body }) => ({
        url: `${prefix}/adm/applications/${publicId}/clarifications/`,
        method: 'POST',
        body,
      }),
    }),
    postAdminCheckItemComment: builder.mutation<
      ApplicationDetail,
      ApplicationPostCheckItemCommentParams
    >({
      transformResponse: (response: ApplicationCheckItemCommentResponse) => response.application,
      query: ({ publicId, kind, body, attachments }) => {
        const files = attachments ?? []
        if (files.length > 0) {
          const form = new FormData()
          form.append('body', body)
          files.forEach((file) => form.append('attachments', file))
          return {
            url: `${prefix}/adm/applications/${publicId}/check-items/${kind}/comments/`,
            method: 'POST',
            body: form,
          }
        }
        return {
          url: `${prefix}/adm/applications/${publicId}/check-items/${kind}/comments/`,
          method: 'POST',
          body: { body },
        }
      },
    }),
    completePreparation: builder.mutation<ApplicationDetail, string>({
      query: (publicId) => ({
        url: `${prefix}/adm/applications/${publicId}/complete-preparation/`,
        method: 'POST',
      }),
    }),
    saveProposalDraft: builder.mutation<ApplicationDetail, ApplicationSaveProposalParams>({
      query: ({ publicId, body }) => ({
        url: `${prefix}/adm/applications/${publicId}/proposal/`,
        method: 'PUT',
        body,
      }),
      transformResponse: applicationFromProposalMutationResponse,
    }),
    previewProposalDraft: builder.mutation<
      ApplicationProposalServicesComparison,
      { publicId: string; serviceLines: { kind: string; amount: string }[] }
    >({
      query: ({ publicId, serviceLines }) => ({
        url: `${prefix}/adm/applications/${publicId}/proposal/preview/`,
        method: 'POST',
        body: { service_lines: serviceLines },
      }),
    }),
    publishProposal: builder.mutation<ApplicationDetail, string>({
      query: (publicId) => ({
        url: `${prefix}/adm/applications/${publicId}/proposal/publish/`,
        method: 'POST',
      }),
      transformResponse: applicationFromProposalMutationResponse,
    }),
    revealContacts: builder.mutation<
      ApplicationDetail,
      ApplicationRevealContactsParams
    >({
      query: ({ publicId, ...body }) => ({
        url: `${prefix}/adm/applications/${publicId}/reveal-contacts/`,
        method: 'POST',
        body,
      }),
    }),
    saveClientContacts: builder.mutation<
      ApplicationDetail,
      ApplicationSaveClientContactsParams
    >({
      query: ({ publicId, ...body }) => ({
        url: `${prefix}/adm/applications/${publicId}/client-contacts/`,
        method: 'PATCH',
        body,
      }),
    }),
    saveExecutorContacts: builder.mutation<
      ApplicationDetail,
      ApplicationSaveExecutorContactsParams
    >({
      query: ({ publicId, ...body }) => ({
        url: `${prefix}/adm/applications/${publicId}/executor-contacts/`,
        method: 'PATCH',
        body,
      }),
    }),
    updateOutcome: builder.mutation<
      ApplicationDetail,
      ApplicationUpdateOutcomeParams
    >({
      query: ({ publicId, ...body }) => ({
        url: `${prefix}/adm/applications/${publicId}/outcome/`,
        method: 'PATCH',
        body,
      }),
    }),
  }),
})

export const {
  useAssignManagerMutation,
  useAssignExecutorMutation,
  useUpdateCheckItemMutation,
  useUploadCheckItemDocumentMutation,
  useDeleteCheckItemDocumentMutation,
  useCreateClarificationMutation,
  usePostAdminCheckItemCommentMutation,
  useCompletePreparationMutation,
  useSaveProposalDraftMutation,
  usePreviewProposalDraftMutation,
  usePublishProposalMutation,
  useRevealContactsMutation,
  useSaveClientContactsMutation,
  useSaveExecutorContactsMutation,
  useUpdateOutcomeMutation,
} = applicationsManagerApi
